// Control Center Tenants list (Blueprint §4.3.2 / D-4) — bound to adminListTenants.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionHeader, StatusBadge, GROUP_LABELS } from "../../../components/control";
import { lifecycleGroup, type LifecycleGroup } from "../../../components/control/lifecycle";
import { adminListTenantsCallable, type AdminTenant } from "../../../lib/adminControlTowerCallables";

const FILTER_GROUPS: (LifecycleGroup | "all")[] = ["all", "onboarding", "ready_trial", "active", "payment_risk", "suspended", "closed"];

export function ControlTenantsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<LifecycleGroup | "all">("all");
  const [search, setSearch] = useState("");
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminListTenantsCallable({ query: search || undefined, limit: 100 });
      setTenants(res.data.tenants ?? []);
      setUpdatedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load tenants.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (filter === "all") return tenants;
    return tenants.filter((t) => lifecycleGroup(t.subscriptionStatus || t.status) === filter);
  }, [tenants, filter]);

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Tenants"
        subtitle="All firms, lifecycle status, and actions."
        updatedAt={updatedAt}
        onRefresh={() => void load()}
        refreshing={loading}
        actions={
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void load()}
            placeholder="Search…"
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm"
          />
        }
      />
      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p> : null}

      <div className="flex flex-wrap gap-2">
        {FILTER_GROUPS.map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => setFilter(g)}
            className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === g ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
          >
            {g === "all" ? "All" : GROUP_LABELS[g]}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="grid grid-cols-[1.6fr,1fr,1fr,0.4fr] gap-2 border-b border-slate-100 px-4 py-2 text-[11px] font-black uppercase tracking-wide text-slate-400">
          <span>Firm</span>
          <span>Lifecycle</span>
          <span>Subscription</span>
          <span />
        </div>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-400">{loading ? "Loading…" : "No tenants match this filter."}</p>
        ) : (
          rows.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => navigate(`/control/tenants/${t.id}`)}
              className="grid w-full grid-cols-[1.6fr,1fr,1fr,0.4fr] items-center gap-2 border-b border-slate-50 px-4 py-3 text-left text-sm hover:bg-slate-50"
            >
              <span className="min-w-0">
                <span className="block truncate font-bold text-slate-900">{t.name || t.id}</span>
                <span className="block truncate text-[11px] text-slate-400">{t.id}</span>
              </span>
              <span><StatusBadge state={t.subscriptionStatus || t.status} /></span>
              <span className="truncate text-slate-500">{t.subscriptionStatus || "—"}</span>
              <span className="text-right text-slate-400">›</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
